// ScheduleTemplateManager.js - Skapa schemamallar med drag-and-drop
//
// Bokningstyperna visas som färgade boxar i en palett. Dra en box in i
// veckorutnätet för att skapa en slot (typens standardlängd förifylls).
// Dra en befintlig slot för att flytta den, klicka för att redigera
// tid/typ eller ta bort. Snabbfyll genererar back-to-back-slots för
// valda veckodagar + tidsintervall. Spara skriver hela veckoschemat
// (PUT ersätter alla slots i mallen).

import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import {
  getScheduleTemplates,
  getScheduleTemplate,
  createScheduleTemplate,
  updateScheduleTemplate,
  deleteScheduleTemplate,
  copyScheduleTemplate,
  getAppointmentTypes
} from './calendarApi';
import './ScheduleTemplateManager.css';

const WEEKDAYS = ['Måndag', 'Tisdag', 'Onsdag', 'Torsdag', 'Fredag', 'Lördag', 'Söndag'];
const WEEKDAYS_SHORT = WEEKDAYS.map(d => d.slice(0, 3));
const START_HOUR = 6;   // Rutnätet börjar 06:00
const END_HOUR = 21;    // ...och slutar 21:00
const PX_PER_MIN = 1;   // 1px per minut → 15h = 900px höga kolumner
const SNAP_MIN = 15;    // Snäpp till 15-minutersgränser

const GRID_MINUTES = (END_HOUR - START_HOUR) * 60;

// 'HH:MM' → minuter sedan midnatt
const toMinutes = (t) => {
  const [h, m] = t.split(':').map(Number);
  return h * 60 + m;
};

// Minuter sedan midnatt → 'HH:MM'
const toTime = (mins) => {
  const h = String(Math.floor(mins / 60)).padStart(2, '0');
  const m = String(mins % 60).padStart(2, '0');
  return `${h}:${m}`;
};

const snap = (mins) => Math.round(mins / SNAP_MIN) * SNAP_MIN;

const clampToGrid = (startMins, durationMins) => {
  const gridStart = START_HOUR * 60;
  const gridEnd = END_HOUR * 60;
  let start = Math.max(gridStart, Math.min(startMins, gridEnd - durationMins));
  return { start, end: start + durationMins };
};

let slotKeyCounter = 1;

const ScheduleTemplateManager = () => {
  const [templates, setTemplates] = useState([]);
  const [types, setTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);

  // Vald mall: null = ingen, 'new' = ny mall, annars template_id
  const [selectedId, setSelectedId] = useState(null);
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  // Slots i klientform: {key, day_of_week, start_time, end_time, appointment_type_id}
  const [slots, setSlots] = useState([]);
  const [dirty, setDirty] = useState(false);
  const [selectedSlotKey, setSelectedSlotKey] = useState(null);

  // Snabbfyll: veckodagar + intervall + typ → back-to-back-slots
  const [fillDays, setFillDays] = useState([true, true, true, true, true, false, false]);
  const [fillStart, setFillStart] = useState('08:00');
  const [fillEnd, setFillEnd] = useState('12:00');
  const [fillTypeId, setFillTypeId] = useState(null);
  const [fillOnline, setFillOnline] = useState(true);
  const [fillInfo, setFillInfo] = useState(null);
  // Håller reda på om själva slotsen ändrats - bara då skickas 'slots'
  // i PUT:en (hel-vecka-ersätt som rensar datumavvikelser)
  const [slotsDirty, setSlotsDirty] = useState(false);

  const typeById = useCallback(
    (id) => types.find(t => t.type_id === id) || null,
    [types]
  );

  // Valt snabbfyll-typ-id, med fallback till första typen om inget
  // valts eller om det valda id:t inte längre finns i types
  const effectiveFillTypeId = types.some(t => t.type_id === fillTypeId)
    ? fillTypeId
    : (types[0]?.type_id ?? null);

  // ── Ladda data ──────────────────────────────────────────────
  const loadAll = async () => {
    try {
      setLoading(true);
      const [tpl, apt] = await Promise.all([getScheduleTemplates(), getAppointmentTypes()]);
      setTemplates(tpl);
      setTypes(apt.filter(t => t.active !== false));
    } catch (err) {
      setError('Kunde inte ladda schemamallar');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadAll(); }, []);

  // ── Välj/skapa mall ─────────────────────────────────────────
  const confirmDiscard = () =>
    !dirty || window.confirm('Du har osparade ändringar. Vill du fortsätta utan att spara?');

  // Lägg in en hämtad mall (med slots) i byggaren
  const openTemplateData = (data) => {
    setSelectedId(data.template_id);
    setName(data.name);
    setDescription(data.description || '');
    setSlots((data.slots || []).map(s => ({
      key: `srv-${s.slot_id}`,
      day_of_week: s.day_of_week,
      start_time: s.start_time,
      end_time: s.end_time,
      appointment_type_id: s.appointment_type_id,
      online_bookable: s.online_bookable !== false
    })));
    setSelectedSlotKey(null);
    setDirty(false);
    setSlotsDirty(false);
    setError(null);
    setFillInfo(null);
  };

  const handleSelectTemplate = async (templateId) => {
    if (!confirmDiscard()) return;
    try {
      openTemplateData(await getScheduleTemplate(templateId));
    } catch (err) {
      setError('Kunde inte ladda mallen');
    }
  };

  // Kopiera mall + slots (inte tilldelningar) och öppna kopian direkt
  const handleCopy = async (templateId) => {
    if (!confirmDiscard()) return;
    try {
      const copy = await copyScheduleTemplate(templateId);
      openTemplateData(copy);
      loadAll();
    } catch (err) {
      setError('Kunde inte kopiera mallen');
    }
  };

  const handleNewTemplate = () => {
    if (!confirmDiscard()) return;
    setSelectedId('new');
    setName('');
    setDescription('');
    setSlots([]);
    setSelectedSlotKey(null);
    setDirty(false);
    setSlotsDirty(false);
    setError(null);
    setFillInfo(null);
  };

  const handleDeactivate = async (templateId) => {
    if (!window.confirm('Inaktivera mallen? Tilldelningar slutar generera schemaslots.')) return;
    try {
      await deleteScheduleTemplate(templateId);
      if (selectedId === templateId) setSelectedId(null);
      loadAll();
    } catch (err) {
      setError('Kunde inte inaktivera mallen');
    }
  };

  // ── Slot-operationer (med överlappskontroll) ────────────────
  const overlaps = (candidate, ignoreKey = null) =>
    slots.some(s =>
      s.key !== ignoreKey &&
      s.day_of_week === candidate.day_of_week &&
      toMinutes(candidate.start_time) < toMinutes(s.end_time) &&
      toMinutes(candidate.end_time) > toMinutes(s.start_time)
    );

  const addSlot = (day, startMins, type) => {
    const duration = type.duration_minutes || 30;
    const { start, end } = clampToGrid(snap(startMins), duration);
    const candidate = {
      key: `new-${slotKeyCounter++}`,
      day_of_week: day,
      start_time: toTime(start),
      end_time: toTime(end),
      appointment_type_id: type.type_id,
      online_bookable: true
    };
    if (overlaps(candidate)) {
      setError('Sloten överlappar en befintlig slot - flytta eller ändra den först');
      return;
    }
    setError(null);
    setSlots(prev => [...prev, candidate]);
    setSelectedSlotKey(candidate.key);
    setDirty(true);
    setSlotsDirty(true);
    setFillInfo(null);
  };

  const moveSlot = (slotKey, day, startMins) => {
    const slot = slots.find(s => s.key === slotKey);
    if (!slot) return;
    const duration = toMinutes(slot.end_time) - toMinutes(slot.start_time);
    const { start, end } = clampToGrid(snap(startMins), duration);
    const candidate = { ...slot, day_of_week: day, start_time: toTime(start), end_time: toTime(end) };
    if (overlaps(candidate, slotKey)) {
      setError('Kan inte flytta dit - sloten överlappar en annan slot');
      return;
    }
    setError(null);
    setSlots(prev => prev.map(s => (s.key === slotKey ? candidate : s)));
    setDirty(true);
    setSlotsDirty(true);
    setFillInfo(null);
  };

  const updateSelectedSlot = (changes) => {
    const slot = slots.find(s => s.key === selectedSlotKey);
    if (!slot) return;
    const candidate = { ...slot, ...changes };
    if (toMinutes(candidate.start_time) >= toMinutes(candidate.end_time)) {
      setError('Starttiden måste vara före sluttiden');
      return;
    }
    if (overlaps(candidate, slot.key)) {
      setError('Ändringen överlappar en annan slot');
      return;
    }
    setError(null);
    setSlots(prev => prev.map(s => (s.key === slot.key ? candidate : s)));
    setDirty(true);
    setSlotsDirty(true);
    setFillInfo(null);
  };

  const removeSelectedSlot = () => {
    setSlots(prev => prev.filter(s => s.key !== selectedSlotKey));
    setSelectedSlotKey(null);
    setDirty(true);
    setSlotsDirty(true);
    setFillInfo(null);
  };

  // ── Snabbfyll ───────────────────────────────────────────────
  // Genererar back-to-back-slots av vald typ för valda dagar inom
  // intervallet. Positioner som överlappar befintliga slots hoppas över.
  const handleQuickFill = () => {
    setFillInfo(null);
    const type = typeById(effectiveFillTypeId);
    if (!type) {
      setError('Välj en bokningstyp för snabbfyll');
      return;
    }
    if (!fillDays.some(Boolean)) {
      setError('Välj minst en veckodag för snabbfyll');
      return;
    }
    if (!fillStart || !fillEnd) {
      setError('Ange både start- och sluttid för snabbfyll');
      return;
    }
    const duration = type.duration_minutes;
    if (!duration || duration <= 0) {
      setError(`Bokningstypen ${type.name} saknar giltig längd - ändra under Bokningstyper`);
      return;
    }
    // Validera de RÅA tiderna före rutnätsklampningen, annars kan
    // klampningen ge ett missvisande "start före slut"-fel
    const rawStart = snap(toMinutes(fillStart));
    const rawEnd = snap(toMinutes(fillEnd));
    if (rawStart >= rawEnd) {
      setError('Starttiden måste vara före sluttiden');
      return;
    }
    const rangeStart = Math.max(rawStart, START_HOUR * 60);
    const rangeEnd = Math.min(rawEnd, END_HOUR * 60);
    const clamped = rangeStart !== rawStart || rangeEnd !== rawEnd;
    if (rangeStart + duration > rangeEnd) {
      setError(
        clamped
          ? `Intervallet ligger utanför rutnätet (${toTime(START_HOUR * 60)}–${toTime(END_HOUR * 60)})`
          : `Intervallet är kortare än en ${type.name} (${duration} min)`
      );
      return;
    }

    const added = [];
    let skipped = 0;
    fillDays.forEach((on, day) => {
      if (!on) return;
      for (let t = rangeStart; t + duration <= rangeEnd; t += duration) {
        const candidate = {
          key: `new-${slotKeyCounter++}`,
          day_of_week: day,
          start_time: toTime(t),
          end_time: toTime(t + duration),
          appointment_type_id: type.type_id,
          online_bookable: fillOnline
        };
        if (overlaps(candidate)) {
          skipped++;
        } else {
          added.push(candidate);
        }
      }
    });

    if (added.length === 0) {
      setError('Inga slots lades till - hela intervallet överlappar befintliga slots');
      return;
    }
    setError(null);
    setSlots(prev => [...prev, ...added]);
    setDirty(true);
    setSlotsDirty(true);
    const notes = [];
    if (skipped > 0) notes.push(`${skipped} hoppades över pga överlapp`);
    if (clamped) notes.push(`begränsat till ${toTime(rangeStart)}–${toTime(rangeEnd)}`);
    setFillInfo(`La till ${added.length} slots` + (notes.length ? ` (${notes.join(', ')})` : ''));
  };

  // ── Drag and drop ───────────────────────────────────────────
  const handlePaletteDragStart = (e, type) => {
    e.dataTransfer.setData('application/json', JSON.stringify({ kind: 'type', typeId: type.type_id }));
    e.dataTransfer.effectAllowed = 'copy';
  };

  const handleSlotDragStart = (e, slot) => {
    // Spara var i sloten användaren grep tag, så flytten känns naturlig
    const rect = e.currentTarget.getBoundingClientRect();
    e.dataTransfer.setData('application/json', JSON.stringify({
      kind: 'slot', slotKey: slot.key, grabOffsetY: e.clientY - rect.top
    }));
    e.dataTransfer.effectAllowed = 'move';
    e.stopPropagation();
  };

  const handleColumnDrop = (e, day) => {
    e.preventDefault();
    let payload;
    try {
      payload = JSON.parse(e.dataTransfer.getData('application/json'));
    } catch {
      return;
    }
    const rect = e.currentTarget.getBoundingClientRect();

    if (payload.kind === 'type') {
      const type = typeById(payload.typeId);
      if (!type) return;
      const mins = START_HOUR * 60 + (e.clientY - rect.top) / PX_PER_MIN;
      addSlot(day, mins, type);
    } else if (payload.kind === 'slot') {
      const mins = START_HOUR * 60 + (e.clientY - rect.top - (payload.grabOffsetY || 0)) / PX_PER_MIN;
      moveSlot(payload.slotKey, day, mins);
    }
  };

  // ── Spara ───────────────────────────────────────────────────
  const handleSave = async () => {
    if (!name.trim()) {
      setError('Mallen behöver ett namn');
      return;
    }
    const payload = {
      name: name.trim(),
      description: description || null
    };
    // Skicka bara 'slots' när de faktiskt ändrats - backend gör
    // hel-vecka-ersätt (och rensar datumavvikelser) så fort nyckeln
    // finns i payloaden, även vid ett rent namnbyte
    if (selectedId === 'new' || slotsDirty) {
      payload.slots = slots.map(({ day_of_week, start_time, end_time, appointment_type_id, online_bookable }) =>
        ({ day_of_week, start_time, end_time, appointment_type_id, online_bookable: online_bookable !== false }));
    }
    try {
      setSaving(true);
      setError(null);
      if (selectedId === 'new') {
        const created = await createScheduleTemplate(payload);
        setSelectedId(created.template_id);
      } else {
        await updateScheduleTemplate(selectedId, payload);
      }
      setDirty(false);
      setSlotsDirty(false);
      setFillInfo(null);
      loadAll();
    } catch (err) {
      setError(err.response?.data?.msg || 'Kunde inte spara mallen');
    } finally {
      setSaving(false);
    }
  };

  // ── Render ──────────────────────────────────────────────────
  const selectedSlot = slots.find(s => s.key === selectedSlotKey) || null;
  const hourLabels = [];
  for (let h = START_HOUR; h <= END_HOUR; h++) hourLabels.push(h);

  if (loading) return <div className="stm-loading">Laddar schemamallar...</div>;

  return (
    <div className="schedule-template-manager">
      <div className="stm-header">
        <h2>Schemamallar</h2>
        <p className="stm-description">
          Bygg återanvändbara veckoscheman: dra en bokningstyp från paletten in i
          rutnätet. Tilldela mallar till behandlare under{' '}
          <Link to="/schedule-assignments">Schematilldelning</Link>.
        </p>
      </div>

      {error && <div className="stm-error">{error}</div>}

      <div className="stm-layout">
        {/* Mall-lista */}
        <div className="stm-sidebar">
          <button className="btn btn-primary stm-new-btn" onClick={handleNewTemplate}>
            <i className="fas fa-plus"></i> Ny mall
          </button>
          <div className="stm-template-list">
            {templates.length === 0 && (
              <p className="stm-empty">Inga mallar än</p>
            )}
            {templates.map(t => (
              <div
                key={t.template_id}
                className={`stm-template-item ${selectedId === t.template_id ? 'selected' : ''} ${!t.active ? 'inactive' : ''}`}
                onClick={() => handleSelectTemplate(t.template_id)}
              >
                <div className="stm-template-info">
                  <span className="stm-template-name">{t.name}</span>
                  <span className="stm-template-meta">
                    {t.slot_count} slots{!t.active && ' · inaktiv'}
                  </span>
                </div>
                <div className="stm-template-actions">
                  <button
                    className="btn-icon"
                    title="Kopiera"
                    onClick={(e) => { e.stopPropagation(); handleCopy(t.template_id); }}
                  >
                    <i className="fas fa-copy"></i>
                  </button>
                  {t.active && (
                    <button
                      className="btn-icon btn-icon-danger"
                      title="Inaktivera"
                      onClick={(e) => { e.stopPropagation(); handleDeactivate(t.template_id); }}
                    >
                      <i className="fas fa-trash"></i>
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Byggare */}
        {selectedId === null ? (
          <div className="stm-placeholder">
            <i className="fas fa-calendar-week"></i>
            <p>Välj en mall i listan eller skapa en ny.</p>
          </div>
        ) : (
          <div className="stm-builder">
            <div className="stm-builder-header">
              <input
                type="text"
                className="stm-name-input"
                placeholder="Mallens namn, t.ex. Heltid standard"
                value={name}
                onChange={e => { setName(e.target.value); setDirty(true); }}
              />
              <input
                type="text"
                className="stm-desc-input"
                placeholder="Beskrivning (valfri)"
                value={description}
                onChange={e => { setDescription(e.target.value); setDirty(true); }}
              />
              <button
                className="btn btn-primary"
                onClick={handleSave}
                disabled={saving || !dirty}
              >
                {saving ? 'Sparar...' : dirty ? 'Spara' : 'Sparad'}
              </button>
            </div>

            {/* Spara med slot-ändringar raderar och återskapar mallens ALLA
                slots → alla datumavvikelser som pekar på mallen rensas.
                Rena namn-/beskrivningsändringar skickar inte 'slots' alls. */}
            {slotsDirty && selectedId !== 'new' && (
              <div className="stm-override-warning">
                <i className="fas fa-exclamation-triangle"></i>{' '}
                Spara ersätter hela veckoschemat — alla datumavvikelser för mallens
                tider (flyttade/borttagna enskilda dagar via högerklick i kalendern)
                rensas.
              </div>
            )}

            {/* Palett med bokningstyper */}
            <div className="stm-palette">
              <span className="stm-palette-label">Dra in i schemat:</span>
              {types.length === 0 && (
                <span className="stm-palette-empty">
                  Inga bokningstyper - skapa under <Link to="/appointment-types">Bokningstyper</Link>
                </span>
              )}
              {types.map(type => (
                <div
                  key={type.type_id}
                  className="stm-type-box"
                  style={{ backgroundColor: type.color }}
                  draggable
                  onDragStart={e => handlePaletteDragStart(e, type)}
                  title={`${type.name} (${type.duration_minutes} min)`}
                >
                  {type.name}
                  <span className="stm-type-duration">{type.duration_minutes}m</span>
                </div>
              ))}
            </div>

            {/* Snabbfyll: generera back-to-back-slots för valda dagar */}
            <div className="stm-quickfill">
              <span className="stm-quickfill-label">
                <i className="fas fa-bolt"></i> Snabbfyll:
              </span>
              <div className="stm-quickfill-days">
                {WEEKDAYS_SHORT.map((d, i) => (
                  <label key={i} className={`stm-day-toggle ${fillDays[i] ? 'on' : ''}`}>
                    <input
                      type="checkbox"
                      checked={fillDays[i]}
                      onChange={() => setFillDays(prev => prev.map((v, j) => (j === i ? !v : v)))}
                    />
                    {d}
                  </label>
                ))}
              </div>
              <input
                type="time"
                step={SNAP_MIN * 60}
                value={fillStart}
                onChange={e => setFillStart(e.target.value)}
              />
              <span>–</span>
              <input
                type="time"
                step={SNAP_MIN * 60}
                value={fillEnd}
                onChange={e => setFillEnd(e.target.value)}
              />
              <select
                value={effectiveFillTypeId ?? ''}
                onChange={e => {
                  const id = parseInt(e.target.value, 10);
                  if (!Number.isNaN(id)) setFillTypeId(id);
                }}
              >
                {types.map(t => (
                  <option key={t.type_id} value={t.type_id}>
                    {t.name} ({t.duration_minutes} min)
                  </option>
                ))}
              </select>
              <label className="stm-online-toggle" title="Ska tiderna gå att boka online (Bokningsappen)?">
                <input
                  type="checkbox"
                  checked={fillOnline}
                  onChange={e => setFillOnline(e.target.checked)}
                />
                Online
              </label>
              <button
                className="btn btn-secondary stm-quickfill-btn"
                onClick={handleQuickFill}
                disabled={types.length === 0}
              >
                Fyll i
              </button>
              {fillInfo && <span className="stm-quickfill-info">{fillInfo}</span>}
            </div>

            {/* Redigeringspanel för vald slot */}
            {selectedSlot && (
              <div className="stm-slot-editor">
                <span className="stm-slot-editor-label">
                  Vald slot ({WEEKDAYS[selectedSlot.day_of_week]}):
                </span>
                <select
                  value={selectedSlot.day_of_week}
                  onChange={e => updateSelectedSlot({ day_of_week: parseInt(e.target.value) })}
                >
                  {WEEKDAYS.map((d, i) => <option key={i} value={i}>{d}</option>)}
                </select>
                <input
                  type="time"
                  step={SNAP_MIN * 60}
                  value={selectedSlot.start_time}
                  onChange={e => e.target.value && updateSelectedSlot({ start_time: e.target.value })}
                />
                <span>–</span>
                <input
                  type="time"
                  step={SNAP_MIN * 60}
                  value={selectedSlot.end_time}
                  onChange={e => e.target.value && updateSelectedSlot({ end_time: e.target.value })}
                />
                <select
                  value={selectedSlot.appointment_type_id}
                  onChange={e => updateSelectedSlot({ appointment_type_id: parseInt(e.target.value) })}
                >
                  {types.map(t => <option key={t.type_id} value={t.type_id}>{t.name}</option>)}
                </select>
                <label className="stm-online-toggle" title="Ska tiden gå att boka online (Bokningsappen)?">
                  <input
                    type="checkbox"
                    checked={selectedSlot.online_bookable !== false}
                    onChange={e => updateSelectedSlot({ online_bookable: e.target.checked })}
                  />
                  Online
                </label>
                <button className="btn-icon btn-icon-danger" title="Ta bort slot" onClick={removeSelectedSlot}>
                  <i className="fas fa-trash"></i>
                </button>
                <button className="btn-icon" title="Avmarkera" onClick={() => setSelectedSlotKey(null)}>
                  <i className="fas fa-times"></i>
                </button>
              </div>
            )}

            {/* Veckorutnät */}
            <div className="stm-grid">
              <div className="stm-time-axis">
                <div className="stm-day-header stm-time-header"></div>
                <div className="stm-time-labels" style={{ height: GRID_MINUTES * PX_PER_MIN }}>
                  {hourLabels.map(h => (
                    <span
                      key={h}
                      className="stm-hour-label"
                      style={{ top: (h - START_HOUR) * 60 * PX_PER_MIN }}
                    >
                      {String(h).padStart(2, '0')}:00
                    </span>
                  ))}
                </div>
              </div>
              {WEEKDAYS.map((dayName, day) => (
                <div key={day} className="stm-day">
                  <div className="stm-day-header">{dayName}</div>
                  <div
                    className="stm-day-column"
                    style={{ height: GRID_MINUTES * PX_PER_MIN }}
                    onDragOver={e => e.preventDefault()}
                    onDrop={e => handleColumnDrop(e, day)}
                    onClick={() => setSelectedSlotKey(null)}
                  >
                    {hourLabels.slice(0, -1).map(h => (
                      <div
                        key={h}
                        className="stm-hour-line"
                        style={{ top: (h - START_HOUR) * 60 * PX_PER_MIN }}
                      />
                    ))}
                    {slots.filter(s => s.day_of_week === day).map(slot => {
                      const type = typeById(slot.appointment_type_id);
                      const top = (toMinutes(slot.start_time) - START_HOUR * 60) * PX_PER_MIN;
                      const height = (toMinutes(slot.end_time) - toMinutes(slot.start_time)) * PX_PER_MIN;
                      return (
                        <div
                          key={slot.key}
                          className={`stm-slot ${selectedSlotKey === slot.key ? 'selected' : ''} ${slot.online_bookable === false ? 'offline' : ''}`}
                          style={{ top, height, backgroundColor: type?.color || '#6c757d' }}
                          draggable
                          onDragStart={e => handleSlotDragStart(e, slot)}
                          onClick={e => { e.stopPropagation(); setSelectedSlotKey(slot.key); }}
                          title={`${type?.name || '?'} ${slot.start_time}-${slot.end_time}${slot.online_bookable === false ? ' (ej online)' : ''}`}
                        >
                          <span className="stm-slot-type">
                            {type?.name || '?'}
                            {slot.online_bookable === false && <i className="fas fa-eye-slash stm-slot-offline-icon"></i>}
                          </span>
                          <span className="stm-slot-time">{slot.start_time}–{slot.end_time}</span>
                        </div>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ScheduleTemplateManager;
